import React, { useEffect, useRef, useState } from 'react';
import { organisationsAPI } from '../../../../organisations/api/organisationsAPI';
import { isForbidden, organisationErrorMessage } from '../../../../organisations/lib/errorMessage';
import SectionState from './SectionState';
import ChatTranscript from './ChatTranscript';
import { dayMonthYear, plural } from './lib/activityFormat';

const MODES = ['All', 'Planning', 'Coach', 'Builder', 'General', 'Onboarding'];
const PAGE = 8;

const titleOf = (row) => (row.title && row.title.trim() ? row.title.trim() : 'Untitled conversation');

/**
 * Every conversation for this student, under the Chat block. Eight rows at
 * a time; the next API page (20) is fetched when the rows on screen run out.
 * Opening a transcript fetches it once and keeps it until the student changes.
 */
const ChatConversations = ({ studentId }) => {
  const cache = useRef(new Map());
  const studentRef = useRef(studentId);
  const [mode, setMode] = useState('All');
  const [openId, setOpenId] = useState(null);
  const [shown, setShown] = useState(PAGE);
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [total, setTotal] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState('');

  if (studentRef.current !== studentId) {
    studentRef.current = studentId;
    cache.current = new Map();
    setMode('All');
    setOpenId(null);
    setShown(PAGE);
    setItems([]);
    setCursor(null);
    setTotal(0);
    setMoreError('');
  }

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setError('');
    setItems([]);
    setCursor(null);
    setShown(PAGE);
    setOpenId(null);
    organisationsAPI.getStudentChatConversations(studentId, { mode: mode === 'All' ? undefined : mode })
      .then((data) => {
        if (cancelled) return;
        const rows = data.conversations || [];
        setItems(rows);
        setCursor(data.next_cursor || null);
        setTotal(data.total != null ? data.total : rows.length);
        setStatus(rows.length ? 'ready' : 'empty');
      })
      .catch((err) => {
        if (cancelled) return;
        if (isForbidden(err)) {
          setStatus('forbidden');
          setError('');
        } else {
          setStatus('error');
          setError(organisationErrorMessage(err, 'Please try again.'));
        }
      });
    return () => { cancelled = true; };
  }, [studentId, mode, attempt]);

  const visible = items.slice(0, shown);
  const remaining = Math.max(0, total - shown);

  const showMore = async () => {
    const nextShown = Math.min(shown + PAGE, total);
    setMoreError('');
    if (nextShown > items.length && cursor) {
      setLoadingMore(true);
      try {
        const page = await organisationsAPI.getStudentChatConversations(studentId, {
          mode: mode === 'All' ? undefined : mode,
          cursor,
        });
        if (studentRef.current !== studentId) return;
        setItems((prev) => [...prev, ...(page.conversations || [])]);
        setCursor(page.next_cursor || null);
        if (page.total != null) setTotal(page.total);
      } catch (err) {
        if (studentRef.current === studentId) {
          setMoreError(organisationErrorMessage(err, 'Could not load more. Please try again.'));
        }
        return;
      } finally {
        if (studentRef.current === studentId) setLoadingMore(false);
      }
    }
    if (studentRef.current === studentId) setShown(nextShown);
  };

  const listState = {
    status,
    data: items,
    error,
    retry: () => setAttempt((n) => n + 1),
  };

  return (
    <div className="td-act-conversations">
      <h4 className="td-act-subtitle">Conversations</h4>
      <p className="td-act-section__sub">
        Every conversation, all time, newest first.
      </p>
      <div className="td-act-toggle" role="group" aria-label="Conversation mode">
        {MODES.map((label) => (
          <button
            key={label}
            type="button"
            aria-pressed={mode === label}
            onClick={() => setMode(label)}
          >
            {label}
          </button>
        ))}
      </div>
      <SectionState
        state={listState}
        what="conversations"
        emptyTitle="No conversations"
        emptyBody="Nothing in this mode yet."
      />
      {status === 'ready' ? (
        <>
          <ul className="td-act-rows">
            {visible.map((row) => {
              const open = openId === row.id;
              const panelId = `td-act-transcript-${row.id}`;
              const started = dayMonthYear(row.started_at);
              return (
                <li key={row.id}>
                  <div className="td-act-rowline">
                    <div className="td-act-rowline__main">
                      <div className="td-act-rowdate">{dayMonthYear(row.last_message_at)}</div>
                      <div>{titleOf(row)}</div>
                      <div className="td-act-meta">
                        {`${row.mode} · ${plural(row.student_messages, 'message')} from the student${started ? ` · started ${started}` : ''}`}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="td-btn td-btn--small"
                      aria-expanded={open}
                      aria-controls={panelId}
                      onClick={() => setOpenId(open ? null : row.id)}
                    >
                      {open ? 'Close' : 'Read'}
                    </button>
                  </div>
                  {open ? (
                    <div id={panelId}>
                      <ChatTranscript
                        studentId={studentId}
                        conversationId={row.id}
                        cache={cache.current}
                      />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {remaining > 0 || moreError ? (
            <div className="td-act-more">
              {moreError ? <p className="td-error" role="alert">{moreError}</p> : null}
              {remaining > 0 ? (
                <button
                  type="button"
                  className="td-btn td-btn--small"
                  onClick={showMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading…' : `Show ${Math.min(PAGE, remaining)} more of ${remaining}`}
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default ChatConversations;
